// IDs sem taxa (Dinheiro, TED/PIX, Pix à distância)
const IDS_SEM_TAXA = new Set([1, 5, 10]);
// IDs com taxa (Cartão de Crédito, Cartão de Débito)
const IDS_COM_TAXA = new Set([3, 4]);

/**
 * Calcula os pontos de fidelidade que o cliente vai ganhar nessa venda.
 *
 * @param {Object} params
 * @param {Array}  params.itens              - itens da venda (para checar liberações)
 * @param {Array}  params.parcelas           - parcelas com tipo_pagamento.id e valor
 * @param {number} params.valorSemTaxa       - R$ por ponto (pix + dinheiro)
 * @param {number} params.valorComTaxa       - R$ por ponto (crédito + débito)
 * @param {string} [params.TIPO_LIBERACAO]   - constante TIPO_OFERTA_PRODUTO_LIBERACAO
 * @returns {number} pontos ganhos (0 se houver liberação de preço)
 */
export function calcularPontosFidelidade({
  itens,
  parcelas,
  valorSemTaxa,
  valorComTaxa,
  TIPO_LIBERACAO,
}) {
  if (
    TIPO_LIBERACAO &&
    itens &&
    itens.some((item) => item.tipo_oferta === TIPO_LIBERACAO)
  ) {
    return 0;
  }

  let pontosDinheiro = 0;
  let pontosCartao = 0;

  for (const parcela of parcelas) {
    const id = parcela.tipo_pagamento.id;
    if (IDS_SEM_TAXA.has(id)) {
      pontosDinheiro += parcela.valor;
    } else if (IDS_COM_TAXA.has(id)) {
      pontosCartao += parcela.valor;
    }
  }

  return (
    Math.floor(pontosDinheiro / valorSemTaxa) +
    Math.floor(pontosCartao / valorComTaxa)
  );
}

/**
 * Estimativa de pontos no modo "itens" (antes de definir o pagamento).
 * Assume o melhor cenário: todo o subtotal pago em pix/dinheiro.
 *
 * @param {Object} params
 * @param {Array}  params.itens        - itens da venda
 * @param {number} params.subTotal     - total da venda
 * @param {number} params.valorSemTaxa - R$ por ponto
 * @param {string} [params.TIPO_LIBERACAO]
 * @returns {number}
 */
export function estimarPontosFidelidade({
  itens,
  subTotal,
  valorSemTaxa,
  TIPO_LIBERACAO,
}) {
  if (
    TIPO_LIBERACAO &&
    itens &&
    itens.some((item) => item.tipo_oferta === TIPO_LIBERACAO)
  ) {
    return 0;
  }
  return Math.floor(subTotal / valorSemTaxa);
}

const NIVEIS_CLASSIFICACAO = [
  { limite: 100, classificacao: "Cobre" },
  { limite: 200, classificacao: "Prata" },
  { limite: 300, classificacao: "Platina" },
  { limite: 400, classificacao: "Ouro" },
  { limite: 500, classificacao: "Ródio" },
  { limite: 600, classificacao: "Irídio" },
  { limite: 700, classificacao: "Paládio" },
  { limite: 800, classificacao: "Jadeíta" },
  { limite: 900, classificacao: "Plutônio" },
  { limite: 1000, classificacao: "Musgravite" },
  { limite: 2000, classificacao: "Painita" },
  { limite: 3000, classificacao: "Alexandrita" },
  { limite: 4000, classificacao: "Esmeralda" },
  { limite: 5000, classificacao: "Ruby" },
  { limite: 10000, classificacao: "Taaffeite" },
  { limite: 20000, classificacao: "Trítio" },
  { limite: 30000, classificacao: "Diamante" },
  { limite: 40000, classificacao: "Diamante Azul" },
  { limite: 50000, classificacao: "Diamante Rosa" },
  { limite: 100000, classificacao: "Califórnio" },
  { limite: 200000, classificacao: "Antimatéria" },
];

export function getClassificacao(score) {
  const nivel = NIVEIS_CLASSIFICACAO.find((n) => score <= n.limite);

  return nivel?.classificacao ?? "Antimatéria";
}

const NIVEIS_SCORE = [
  100, 200, 300, 400, 500, 600, 700, 800, 900, 1000,
  2000, 3000, 4000, 5000, 10000, 20000, 30000, 40000, 50000, 100000, 200000,
];

/**
 * Retorna quanto falta para o próximo nível de classificação.
 * @param {number} score - pontuação atual
 * @returns {{ faltam: number, classificacao: string, meta: number } | null}
 */
export function getProximoNivel(score) {
  const indiceAtual = NIVEIS_CLASSIFICACAO.findIndex(
    (n) => score <= n.limite
  );

  if (indiceAtual === -1) return null;

  const proximoNivel = NIVEIS_CLASSIFICACAO[indiceAtual + 1];

  if (!proximoNivel) return null;

  const limiteAtual = NIVEIS_CLASSIFICACAO[indiceAtual].limite;
  const meta = limiteAtual + 1;

  return {
    faltam: meta - score,
    classificacao: proximoNivel.classificacao,
    meta,
  };
}
